import { useClientPortalAPI, useQueryParams } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { EVENT_NAMES, useEventTracking } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useState, useMemo } from "react";

import {
  OrderContext,
  SelectedCheckAddOn,
  SelectedChecksType,
} from "../../OrderContext";
import {
  getAvailableSelectOptions,
  getCheckPrice,
  getSelectedChecksExclMutuallyExclusiveInclRequired,
  getSelectedChecksExclRemovedExclStandalone,
  getUpdatedChecks,
  updateCheckArguments,
  syncMutuallyInclusiveAddOns,
  resolveUkDbsCrossCardConflicts,
} from "../../order-utils";
import { CheckData } from "../../types";
import { resolvePriceIsCatalogMinimum } from "../../../../utils/resolvePriceIsCatalogMinimum";
import { applyHideCcrcAndCanadianCreditQuickscreenFilter } from "./hideCcrcAndCanadianCreditQuickscreenFilter";

type ChecksByCategory = Record<string, CheckData[]>;

const sortCategories = (
  categories: ChecksByCategory,
  sortDirection: "ascending" | "descending" = "ascending"
): ChecksByCategory => {
  return Object.entries(categories)
    .sort(([categoryA], [categoryB]) => {
      const comparison = categoryA.localeCompare(categoryB);
      return sortDirection === "ascending" ? comparison : -comparison;
    })
    .reduce((acc, [category, checks]) => {
      acc[category] = checks;
      return acc;
    }, {} as ChecksByCategory);
};

const filterCategoriesBySearch = (
  categories: ChecksByCategory,
  searchTerm: string
): ChecksByCategory => {
  if (!searchTerm) return categories;
  return Object.fromEntries(
    Object.entries(categories).filter(([key]) => key.includes(searchTerm))
  );
};

const filterChecksByNameOrDescription = (
  categories: ChecksByCategory,
  searchTerm: string
): ChecksByCategory => {
  if (!searchTerm) return categories;

  return Object.fromEntries(
    Object.entries(categories)
      .map(([category, checks]) => [
        category,
        checks.filter((check) => {
          let descriptionText = "";
          if (Array.isArray(check.description)) {
            descriptionText = check.description.join(" ");
          } else if (typeof check.description === "string") {
            descriptionText = check.description;
          }
          return (
            descriptionText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            check.name?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }),
      ])
      .filter(([_, checks]) => checks.length > 0)
  );
};

/**
 * Returns add-on arguments that should be inherited by a newly selected check.
 * When a sibling check already has a shared add-on enabled (e.g. CV Review),
 * the new check inherits that state so mutually inclusive add-ons stay in sync.
 */
export const getInheritedAddOnArgs = (
  existingChecks: readonly SelectedChecksType[],
  newCheckAddOnOptions?: readonly SelectedCheckAddOn[]
): Record<string, boolean> => {
  if (!newCheckAddOnOptions?.length) return {};

  const eligibleKeys = new Set(newCheckAddOnOptions.map((a) => a.key));

  return existingChecks.reduce<Record<string, boolean>>((acc, check) => {
    for (const [key, value] of Object.entries(check.check_arguments)) {
      if (value === true && eligibleKeys.has(key) && !acc[key]) {
        acc[key] = true;
      }
    }
    return acc;
  }, {});
};

export function useCheckSectionData(
  group: string | null,
  quickscreenOnly: boolean
) {
  const { params, setParams } = useQueryParams();
  const { trackEvent } = useEventTracking();
  const [baseChecksByCategory, setBaseChecksByCategory] =
    useState<ChecksByCategory>({});
  const {
    clientPortalOrderSelectionNewCategoryView,
    clientPortalHideCcrcAndCanadianCreditForQuickscreen,
  } = useFlags();
  const { order, setOrder } = useContext(OrderContext);

  // Set default sort parameter if not present
  useEffect(() => {
    if (!params.sort) {
      setParams({ ...params, sort: "ascending" });
    }
  }, [params, setParams]);

  const { data: rawAvailableChecks, isLoading } = useClientPortalAPI(
    group
      ? `/api/internal/client-portal/customer-groups/${group}/available-checks/`
      : null,
    { is_quickscreen: quickscreenOnly ?? false }
  );

  // FP-1018: When the hide flag is ON and we're on the quickscreen tab, apply
  // the UI-only filter to the raw available-checks response *before* it is
  // consumed downstream. CheckSection renders both directly from
  // `availableChecks` (default view) and from `checksGroupedByCategory` (new
  // category view), so the filter must live at the source of both.
  const availableChecks = useMemo<CheckData[] | undefined>(() => {
    if (!rawAvailableChecks) return rawAvailableChecks;
    if (
      !quickscreenOnly ||
      !clientPortalHideCcrcAndCanadianCreditForQuickscreen
    ) {
      return rawAvailableChecks;
    }
    return applyHideCcrcAndCanadianCreditQuickscreenFilter(rawAvailableChecks);
  }, [
    rawAvailableChecks,
    quickscreenOnly,
    clientPortalHideCcrcAndCanadianCreditForQuickscreen,
  ]);

  // Process available checks into categories
  useEffect(() => {
    if (!availableChecks) return;

    const groupedChecks = availableChecks.reduce(
      (acc: ChecksByCategory, check: CheckData) => {
        const type = check.category || "Other";
        if (!acc[type]) acc[type] = [];
        acc[type].push(check);
        return acc;
      },
      {}
    );
    setBaseChecksByCategory(sortCategories(groupedChecks));
  }, [availableChecks]);

  // Apply sorting and filtering based on params
  const checksGroupedByCategory = useMemo(() => {
    let result = baseChecksByCategory;

    if (params.sort) {
      result = sortCategories(
        result,
        params.sort as "ascending" | "descending"
      );
    }

    if (params.category) {
      result = filterCategoriesBySearch(result, params.category as string);
    }

    if (params.search) {
      result = filterChecksByNameOrDescription(result, params.search as string);
    }

    return result;
  }, [baseChecksByCategory, params.sort, params.category, params.search]);

  const bundleChecks = useMemo(
    () =>
      order.bundles.flatMap(
        ({ checks }) =>
          checks?.map((check: { type: string }) => check.type) || []
      ),
    [order.bundles]
  );

  const handleCheckSelect = (check: CheckData) => {
    const isRemoving = !!order.checks.find(({ key }) => key === check.type);
    trackEvent(EVENT_NAMES.CP_CHECK_TYPE, {
      checkType: check.type,
      action: isRemoving ? "remove" : "add",
    });

    if (isRemoving) {
      setOrder({
        ...order,
        checks: getSelectedChecksExclRemovedExclStandalone(order.checks, check),
        group: group ?? "",
        tags: [],
      });
      return;
    }

    // If the check has options, then automatically select the first option
    const firstOption = getAvailableSelectOptions(
      check.select_options ?? []
    )?.[0];
    const requiredChildChecks = firstOption?.required_check_types || [];
    const requiredChecksToOrder = [
      ...(check.required_check_types || []),
      ...requiredChildChecks,
    ];

    // FP-1018: pass `rawAvailableChecks` (unfiltered) to lookups that index
    // into `order.checks`. The kill-switch flag can flip ON mid-session
    // (LaunchDarkly streams updates), in which case `order.checks` may still
    // hold a hidden type (e.g. CCRC selected before the flip). The helper
    // does `availableChecks.find(...)!` on those keys, and the non-null
    // assertion would crash if we passed the filtered list. Display still
    // uses the filtered `availableChecks` everywhere else.
    const newChecks = [
      ...getSelectedChecksExclMutuallyExclusiveInclRequired(
        rawAvailableChecks || [],
        order.checks,
        check,
        requiredChecksToOrder
      ),
      {
        key: check.type,
        name: check.name,
        check_arguments: getInheritedAddOnArgs(
          order.checks,
          check.add_on_options
        ),

        cost: getCheckPrice(check, firstOption),
        currency: check.currency,
        children: check.children,
        required_check_types: requiredChecksToOrder,
        must_be_ordered_with_one_of: check.must_be_ordered_with_one_of,
        parent_type: check.parent_check_type || undefined,
        ordering_type: check.ordering_type || undefined,
        price_is_catalog_minimum: resolvePriceIsCatalogMinimum(
          check,
          firstOption
        ),
        ...(firstOption
          ? { selectedOptions: [firstOption], selected_options: [firstOption] }
          : {}),
        add_on_options: check.add_on_options?.map((addon) => ({
          key: addon.key,
          name: addon.name ?? addon.label,
          price: addon.price,
          currency: addon.currency,
        })),
      },
    ];
    setOrder({
      ...order,
      checks: firstOption
        ? resolveUkDbsCrossCardConflicts(newChecks, check.type, [firstOption])
        : newChecks,
      group: group ?? "",
      tags: [],
    });
  };

  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: CheckData,
    selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][]
  ) => {
    setOrder((prev) => {
      // FP-1018: see note in `handleCheckSelect`; pass the unfiltered
      // `rawAvailableChecks` here too so lookups against `prev.checks`
      // don't crash if the kill-switch flag flipped ON mid-session.
      const updatedChecks = getUpdatedChecks(
        check_arguments,
        rawAvailableChecks || [],
        prev.checks,
        check,
        selectedOptions
      );
      return {
        ...prev,
        checks: resolveUkDbsCrossCardConflicts(
          updatedChecks,
          check.type,
          selectedOptions
        ),
      };
    });
  };

  /**
   * Updates only check_arguments for a given check without recalculating cost or
   * touching selectedOptions. Used for add-on toggles (e.g. CV Review) where the
   * selection/pricing state should remain unchanged.
   * Also syncs mutually inclusive add-on state to sibling checks that declare
   * the same add-on key.
   */
  const handleUpdateCheckAddOns = (
    checkType: string,
    checkArguments: Record<string, unknown>
  ) => {
    setOrder((prev) => ({
      ...prev,
      checks: syncMutuallyInclusiveAddOns(
        updateCheckArguments(prev.checks, checkType, checkArguments),
        checkType,
        checkArguments
      ),
    }));
  };

  const totalChecksCount = useMemo(() => {
    return Object.values(checksGroupedByCategory).reduce(
      (total, checks) => total + checks.length,
      0
    );
  }, [checksGroupedByCategory]);

  return {
    isLoading,
    availableChecks,
    checksGroupedByCategory,
    order,
    bundleChecks,
    clientPortalOrderSelectionNewCategoryView,
    categories: Object.keys(baseChecksByCategory),
    handleCheckSelect,
    handleUpdateArguments,
    handleUpdateCheckAddOns,
    totalChecksCount,
  };
}
